import { t } from 'i18next'
import { z } from 'zod'

import { DeliveryPeriodType } from '@enechain/eposition-proto/types/v1/deal_pb'

import { isDecFeb } from '~/pages/Simulation/models/SimulationOrderPatternHelper'

import type { LooseTransactionSchema, OrderPatternSchema } from './index'

type ValidationContext = {
  addIssue: (issue: z.IssueData) => void
}

export function isMultiMonthPhysicalFuture(order: OrderPatternSchema): boolean {
  return (
    order.baseSummaryOrder.deliveryPeriodType ===
      DeliveryPeriodType.MULTI_MONTH && order.isPhysicalFuture === true
  )
}

export function isNonDecFebPeriod(order: OrderPatternSchema): boolean {
  return !isDecFeb(
    order.baseSummaryOrder.deliveryDateRange?.deliveryStartDate,
    order.baseSummaryOrder.deliveryDateRange?.deliveryEndDate,
  )
}

export function getSelectedOrderIndexes(
  availableDealSimulationRelation: number[],
  marginSimulationRelation: number[][],
): Set<number> {
  const selectedOrderIndices = new Set<number>()
  for (const relation of marginSimulationRelation) {
    for (const orderNumber of relation) {
      if (orderNumber > 0) {
        selectedOrderIndices.add(orderNumber - 1)
      }
    }
  }
  for (const orderNumber of availableDealSimulationRelation) {
    if (orderNumber > 0) {
      selectedOrderIndices.add(orderNumber - 1)
    }
  }

  return selectedOrderIndices
}

function needLegSeparation({
  isPhysicalFuture,
  summaryOrder,
}: {
  isPhysicalFuture: boolean
  summaryOrder: LooseTransactionSchema
}): boolean {
  if (isPhysicalFuture === false) {
    return false
  }

  if (summaryOrder.deliveryPeriodType !== DeliveryPeriodType.MULTI_MONTH) {
    return false
  }

  if (
    isDecFeb(
      summaryOrder.deliveryDateRange?.deliveryStartDate,
      summaryOrder.deliveryDateRange?.deliveryEndDate,
    )
  ) {
    return false
  }

  return true
}

export function validateBaseTransactions(
  order: OrderPatternSchema,
  index: number,
  ctx: ValidationContext,
): void {
  if (
    needLegSeparation({
      summaryOrder: order.baseSummaryOrder,
      isPhysicalFuture: order.isPhysicalFuture,
    })
  ) {
    if (
      order.baseTransactions === undefined ||
      order.baseTransactions.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('validation:errors.custom.leg_required_for_multi_month'),
        path: ['orderPattern', index, 'baseTransactions'],
      })
    }
  }

  return
}

export function validateExchangeTransactions(
  order: OrderPatternSchema,
  index: number,
  ctx: ValidationContext,
): void {
  if (
    order.spreadType.isSpreadTrade === false ||
    order.exchangeSummaryOrder === undefined
  ) {
    return
  }

  if (
    needLegSeparation({
      summaryOrder: order.exchangeSummaryOrder,
      isPhysicalFuture: order.isPhysicalFuture,
    })
  ) {
    if (
      order.exchangeTransactions === undefined ||
      order.exchangeTransactions.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('validation:errors.custom.leg_required_for_multi_month'),
        path: ['orderPattern', index, 'exchangeTransactions'],
      })
    }
  }
}
